import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';
import { Settings, Save, DollarSign, Users, ShoppingCart, Trophy, Percent, Loader, AlertCircle, CheckCircle } from 'lucide-react';

const AdminSettings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [settings, setSettings] = useState({
    // Referral Settings
    referralLevel1: 8,
    referralLevel2: 5,
    referralLevel3: 2,
    
    // Marketplace Settings
    marketplaceFee: 10,
    marketplaceCompanyShare: 85,
    marketplaceReferralShare: 15,
    saleExpiryDays: 7,
    tradeExpiryDays: 7,
    
    // Tournament Settings
    buyInCompanyShare: 10,
    buyInPrizePoolShare: 90,
    
    // Pack Settings
    packPrizePoolShare: 40,
    packReferralShare: 15,
    starterPackCards: 30,
    
    // Auction Settings
    auctionFee: 10,
    auctionMinIncrement: 5,
    auctionDurationHours: 24,
    auctionTimeExtensionSeconds: 30,
    auctionDefaultPriceBasic: 1,
    auctionDefaultPricePlus: 5,
    auctionDefaultPriceRare: 10,
    auctionDefaultPriceEpic: 20,
    auctionDefaultPriceUnique: 50,
    auctionDefaultPriceDivinity: 100,
    auctionDefaultPriceLegend: 100,
    
    // Loan Settings
    loanDurations: [1, 3, 5, 10], // Gameweeks
    
    // Card Settings
    cardXPIncrement: 0.5,
    cardMaxXP: 5.0,
    cardCopyLimitPlus: 1000,
    cardCopyLimitRare: 100,
    cardCopyLimitEpic: 10,
    cardCopyLimitUnique: 1,
    cardCopyLimitLegend: 1,
    cardCopyLimitDivinity: 1,
    
    // Prize Distribution
    prizeFirst: 20,
    prizeSecond: 15,
    prizeThird: 10,
    prizeFourth: 8,
    prizeFifth: 7,
    prizeWinnersPercentage: 10,
    prizeWinnersMax: 50,
    
    // Category Prize Distribution
    categoryBasic: 0,
    categoryPlus: 13,
    categoryRare: 22,
    categoryEpic: 30,
    categoryUnique: 35,
    categoryPrizePoolShare: 50,
    monthlyRankingsShare: 50,
    
    // Time Periods
    recentEarningsDays: 30,
    activityReportDays: 7,
    
    // Display Limits
    topReferrersLimit: 10,
    packHistoryLimit: 50,
    tournamentRankingsLimit: 100,
    transactionHistoryLimit: 50,
    
    // Withdrawal Settings
    minWithdrawalAmount: 0,
    maxWithdrawalAmount: 0, // 0 = no limit
    
    // Gameweek Settings
    autoGenerateGameweeks: true,
    autoGenerateRankings: true,
    autoGenerateWeeksCount: 4
  });

  const [originalSettings, setOriginalSettings] = useState({});

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getAuctionDefaultPrices();
      if (res?.data?.success && res.data.prices) {
        setSettings((prev) => ({ ...prev, ...res.data.prices }));
        setOriginalSettings((prev) => ({ ...prev, ...res.data.prices }));
      } else {
        setOriginalSettings({ ...settings });
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      showMessage('error', 'Failed to load settings');
      setOriginalSettings({ ...settings });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const auctionPrices = {
        auctionDefaultPriceBasic: settings.auctionDefaultPriceBasic,
        auctionDefaultPricePlus: settings.auctionDefaultPricePlus,
        auctionDefaultPriceRare: settings.auctionDefaultPriceRare,
        auctionDefaultPriceEpic: settings.auctionDefaultPriceEpic,
        auctionDefaultPriceUnique: settings.auctionDefaultPriceUnique,
        auctionDefaultPriceLegend: settings.auctionDefaultPriceLegend,
        auctionDefaultPriceDivinity: settings.auctionDefaultPriceDivinity
      };
      await adminApi.saveAuctionDefaultPrices(auctionPrices);
      setOriginalSettings({ ...settings });
      showMessage('success', 'Settings saved successfully');
    } catch (err) {
      console.error('Error saving settings:', err);
      showMessage('error', err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings to default values?')) {
      setSettings({ ...originalSettings });
      showMessage('info', 'Settings reset to last saved values');
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold flex items-center">
          <Settings className="w-8 h-8 mr-3 text-purple-400" />
          Admin Settings
        </h1>
        <div className="flex gap-3">
          <button
            onClick={handleReset}
            disabled={!hasChanges || saving}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {saving ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-900/50 border-green-700 text-green-300' 
            : message.type === 'info'
            ? 'bg-blue-900/50 border-blue-700 text-blue-300'
            : 'bg-red-900/50 border-red-700 text-red-300'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-2" />
            )}
            {message.text}
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Referral Settings */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center mb-4">
            <Users className="w-5 h-5 mr-2 text-purple-400" />
            <h2 className="text-xl font-semibold">Referral Settings</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Configure referral commission percentages for each level
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Level 1 (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.referralLevel1}
                  onChange={(e) => setSettings({ ...settings, referralLevel1: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Level 2 (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.referralLevel2}
                  onChange={(e) => setSettings({ ...settings, referralLevel2: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Level 3 (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.referralLevel3}
                  onChange={(e) => setSettings({ ...settings, referralLevel3: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Marketplace Settings */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center mb-4">
            <ShoppingCart className="w-5 h-5 mr-2 text-blue-400" />
            <h2 className="text-xl font-semibold">Marketplace Settings</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Configure marketplace fees and revenue distribution
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Marketplace Fee (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.marketplaceFee}
                  onChange={(e) => setSettings({ ...settings, marketplaceFee: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Company Share (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.marketplaceCompanyShare}
                  onChange={(e) => setSettings({ ...settings, marketplaceCompanyShare: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Referral Share (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.marketplaceReferralShare}
                  onChange={(e) => setSettings({ ...settings, marketplaceReferralShare: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tournament Settings */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center mb-4">
            <Trophy className="w-5 h-5 mr-2 text-yellow-400" />
            <h2 className="text-xl font-semibold">Tournament Settings</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Configure buy-in revenue distribution
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Company Share (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.buyInCompanyShare}
                  onChange={(e) => setSettings({ ...settings, buyInCompanyShare: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Prize Pool Share (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.buyInPrizePoolShare}
                  onChange={(e) => setSettings({ ...settings, buyInPrizePoolShare: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Pack Settings */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center mb-4">
            <ShoppingCart className="w-5 h-5 mr-2 text-green-400" />
            <h2 className="text-xl font-semibold">Pack Settings</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Configure pack revenue distribution
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Prize Pool Share (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.packPrizePoolShare}
                  onChange={(e) => setSettings({ ...settings, packPrizePoolShare: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Referral Share (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.packReferralShare}
                  onChange={(e) => setSettings({ ...settings, packReferralShare: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Auction Settings */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center mb-4">
            <DollarSign className="w-5 h-5 mr-2 text-orange-400" />
            <h2 className="text-xl font-semibold">Auction Settings</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Configure auction fees and bid increments
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Auction Fee (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.auctionFee}
                  onChange={(e) => setSettings({ ...settings, auctionFee: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Minimum Bid Increment (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.auctionMinIncrement}
                  onChange={(e) => setSettings({ ...settings, auctionMinIncrement: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Duration (Hours)</label>
              <input
                type="number"
                min="1"
                max="168"
                value={settings.auctionDurationHours}
                onChange={(e) => setSettings({ ...settings, auctionDurationHours: parseInt(e.target.value) || 24 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Time Extension (Seconds)</label>
              <input
                type="number"
                min="0"
                max="300"
                value={settings.auctionTimeExtensionSeconds}
                onChange={(e) => setSettings({ ...settings, auctionTimeExtensionSeconds: parseInt(e.target.value) || 30 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-300 mb-3">Default Auction Prices (DIOS)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Basic</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={settings.auctionDefaultPriceBasic}
                  onChange={(e) => setSettings({ ...settings, auctionDefaultPriceBasic: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Plus</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={settings.auctionDefaultPricePlus}
                  onChange={(e) => setSettings({ ...settings, auctionDefaultPricePlus: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Rare</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={settings.auctionDefaultPriceRare}
                  onChange={(e) => setSettings({ ...settings, auctionDefaultPriceRare: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Epic</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={settings.auctionDefaultPriceEpic}
                  onChange={(e) => setSettings({ ...settings, auctionDefaultPriceEpic: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Unique</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={settings.auctionDefaultPriceUnique}
                  onChange={(e) => setSettings({ ...settings, auctionDefaultPriceUnique: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Divinity</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={settings.auctionDefaultPriceDivinity}
                  onChange={(e) => setSettings({ ...settings, auctionDefaultPriceDivinity: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Legend</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={settings.auctionDefaultPriceLegend}
                  onChange={(e) => setSettings({ ...settings, auctionDefaultPriceLegend: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Marketplace Expiry Settings */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center mb-4">
            <ShoppingCart className="w-5 h-5 mr-2 text-blue-400" />
            <h2 className="text-xl font-semibold">Marketplace Expiry Settings</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Configure expiry times for marketplace listings
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Sale Expiry (Days)</label>
              <input
                type="number"
                min="1"
                max="30"
                value={settings.saleExpiryDays}
                onChange={(e) => setSettings({ ...settings, saleExpiryDays: parseInt(e.target.value) || 7 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Trade Expiry (Days)</label>
              <input
                type="number"
                min="1"
                max="30"
                value={settings.tradeExpiryDays}
                onChange={(e) => setSettings({ ...settings, tradeExpiryDays: parseInt(e.target.value) || 7 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>
        </div>

        {/* Loan Settings */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center mb-4">
            <DollarSign className="w-5 h-5 mr-2 text-green-400" />
            <h2 className="text-xl font-semibold">Loan Settings</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Configure available loan durations (in gameweeks)
          </p>
          <div className="space-y-2">
            {settings.loanDurations.map((duration, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={duration}
                  onChange={(e) => {
                    const newDurations = [...settings.loanDurations];
                    newDurations[index] = parseInt(e.target.value) || 1;
                    setSettings({ ...settings, loanDurations: newDurations });
                  }}
                  className="w-24 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="text-gray-400">Gameweeks</span>
                {settings.loanDurations.length > 1 && (
                  <button
                    onClick={() => {
                      const newDurations = settings.loanDurations.filter((_, i) => i !== index);
                      setSettings({ ...settings, loanDurations: newDurations });
                    }}
                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={() => {
                if (settings.loanDurations.length < 10) {
                  setSettings({ ...settings, loanDurations: [...settings.loanDurations, 1] });
                }
              }}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm"
            >
              Add Duration
            </button>
          </div>
        </div>

        {/* Card Settings */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center mb-4">
            <Trophy className="w-5 h-5 mr-2 text-purple-400" />
            <h2 className="text-xl font-semibold">Card Settings</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Configure card XP and copy limits
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">XP Increment per Game (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="10"
                  step="0.1"
                  value={settings.cardXPIncrement}
                  onChange={(e) => setSettings({ ...settings, cardXPIncrement: parseFloat(e.target.value) || 0.5 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Max XP (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="10"
                  step="0.1"
                  value={settings.cardMaxXP}
                  onChange={(e) => setSettings({ ...settings, cardMaxXP: parseFloat(e.target.value) || 5.0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Copy Limit - Plus</label>
              <input
                type="number"
                min="1"
                max="10000"
                value={settings.cardCopyLimitPlus}
                onChange={(e) => setSettings({ ...settings, cardCopyLimitPlus: parseInt(e.target.value) || 1000 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Copy Limit - Rare</label>
              <input
                type="number"
                min="1"
                max="10000"
                value={settings.cardCopyLimitRare}
                onChange={(e) => setSettings({ ...settings, cardCopyLimitRare: parseInt(e.target.value) || 100 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Copy Limit - Epic</label>
              <input
                type="number"
                min="1"
                max="10000"
                value={settings.cardCopyLimitEpic}
                onChange={(e) => setSettings({ ...settings, cardCopyLimitEpic: parseInt(e.target.value) || 10 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Copy Limit - Unique</label>
              <input
                type="number"
                min="1"
                max="10000"
                value={settings.cardCopyLimitUnique}
                onChange={(e) => setSettings({ ...settings, cardCopyLimitUnique: parseInt(e.target.value) || 1 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Copy Limit - Legend</label>
              <input
                type="number"
                min="1"
                max="10000"
                value={settings.cardCopyLimitLegend}
                onChange={(e) => setSettings({ ...settings, cardCopyLimitLegend: parseInt(e.target.value) || 1 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Copy Limit - Divinity</label>
              <input
                type="number"
                min="1"
                max="10000"
                value={settings.cardCopyLimitDivinity}
                onChange={(e) => setSettings({ ...settings, cardCopyLimitDivinity: parseInt(e.target.value) || 1 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm text-gray-400 mb-2">Starter Pack Cards</label>
            <input
              type="number"
              min="1"
              max="100"
              value={settings.starterPackCards}
              onChange={(e) => setSettings({ ...settings, starterPackCards: parseInt(e.target.value) || 30 })}
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
            />
          </div>
        </div>

        {/* Prize Distribution */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center mb-4">
            <Trophy className="w-5 h-5 mr-2 text-yellow-400" />
            <h2 className="text-xl font-semibold">Prize Distribution</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Configure prize percentages for top positions
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">1st Place (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.prizeFirst}
                  onChange={(e) => setSettings({ ...settings, prizeFirst: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">2nd Place (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.prizeSecond}
                  onChange={(e) => setSettings({ ...settings, prizeSecond: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">3rd Place (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.prizeThird}
                  onChange={(e) => setSettings({ ...settings, prizeThird: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">4th Place (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.prizeFourth}
                  onChange={(e) => setSettings({ ...settings, prizeFourth: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">5th Place (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.prizeFifth}
                  onChange={(e) => setSettings({ ...settings, prizeFifth: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Prize Winners (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.prizeWinnersPercentage}
                  onChange={(e) => setSettings({ ...settings, prizeWinnersPercentage: parseFloat(e.target.value) || 10 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Max Prize Winners</label>
              <input
                type="number"
                min="1"
                max="1000"
                value={settings.prizeWinnersMax}
                onChange={(e) => setSettings({ ...settings, prizeWinnersMax: parseInt(e.target.value) || 50 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>
        </div>

        {/* Category Prize Distribution */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center mb-4">
            <Trophy className="w-5 h-5 mr-2 text-yellow-400" />
            <h2 className="text-xl font-semibold">Category Prize Distribution</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Configure prize distribution percentages by card category
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Basic (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.categoryBasic}
                  onChange={(e) => setSettings({ ...settings, categoryBasic: parseFloat(e.target.value) || 13 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Plus (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.categoryPlus}
                  onChange={(e) => setSettings({ ...settings, categoryPlus: parseFloat(e.target.value) || 22 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Rare (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.categoryRare}
                  onChange={(e) => setSettings({ ...settings, categoryRare: parseFloat(e.target.value) || 30 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Epic (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.categoryEpic}
                  onChange={(e) => setSettings({ ...settings, categoryEpic: parseFloat(e.target.value) || 30 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Unique (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.categoryUnique}
                  onChange={(e) => setSettings({ ...settings, categoryUnique: parseFloat(e.target.value) || 35 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Category Prize Pool Share (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.categoryPrizePoolShare}
                  onChange={(e) => setSettings({ ...settings, categoryPrizePoolShare: parseFloat(e.target.value) || 50 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Monthly Rankings Share (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.monthlyRankingsShare}
                  onChange={(e) => setSettings({ ...settings, monthlyRankingsShare: parseFloat(e.target.value) || 50 })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <span className="absolute right-3 top-2.5 text-gray-400">%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Time Periods */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center mb-4">
            <Settings className="w-5 h-5 mr-2 text-blue-400" />
            <h2 className="text-xl font-semibold">Time Periods</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Configure time periods for reports and statistics
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Recent Earnings Period (Days)</label>
              <input
                type="number"
                min="1"
                max="365"
                value={settings.recentEarningsDays}
                onChange={(e) => setSettings({ ...settings, recentEarningsDays: parseInt(e.target.value) || 30 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Activity Report Period (Days)</label>
              <input
                type="number"
                min="1"
                max="365"
                value={settings.activityReportDays}
                onChange={(e) => setSettings({ ...settings, activityReportDays: parseInt(e.target.value) || 7 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>
        </div>

        {/* Display Limits */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center mb-4">
            <Settings className="w-5 h-5 mr-2 text-purple-400" />
            <h2 className="text-xl font-semibold">Display Limits</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Configure default limits for lists and rankings
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Top Referrers</label>
              <input
                type="number"
                min="1"
                max="100"
                value={settings.topReferrersLimit}
                onChange={(e) => setSettings({ ...settings, topReferrersLimit: parseInt(e.target.value) || 10 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Pack History</label>
              <input
                type="number"
                min="1"
                max="500"
                value={settings.packHistoryLimit}
                onChange={(e) => setSettings({ ...settings, packHistoryLimit: parseInt(e.target.value) || 50 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Tournament Rankings</label>
              <input
                type="number"
                min="1"
                max="1000"
                value={settings.tournamentRankingsLimit}
                onChange={(e) => setSettings({ ...settings, tournamentRankingsLimit: parseInt(e.target.value) || 100 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Transaction History</label>
              <input
                type="number"
                min="1"
                max="500"
                value={settings.transactionHistoryLimit}
                onChange={(e) => setSettings({ ...settings, transactionHistoryLimit: parseInt(e.target.value) || 50 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>
        </div>

        {/* Withdrawal Settings */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center mb-4">
            <DollarSign className="w-5 h-5 mr-2 text-green-400" />
            <h2 className="text-xl font-semibold">Withdrawal Settings</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Configure withdrawal limits (0 = no limit)
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Minimum Withdrawal Amount (DIOS)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={settings.minWithdrawalAmount}
                onChange={(e) => setSettings({ ...settings, minWithdrawalAmount: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Maximum Withdrawal Amount (DIOS)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={settings.maxWithdrawalAmount}
                onChange={(e) => setSettings({ ...settings, maxWithdrawalAmount: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">0 = no limit</p>
            </div>
          </div>
        </div>

        {/* General Settings */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center mb-4">
            <Settings className="w-5 h-5 mr-2 text-gray-400" />
            <h2 className="text-xl font-semibold">General Settings</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Configure automatic generation settings
          </p>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-900 rounded-lg">
              <div>
                <label className="text-white font-medium">Auto-generate Gameweeks</label>
                <p className="text-sm text-gray-400">Automatically create gameweeks when needed</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.autoGenerateGameweeks}
                  onChange={(e) => setSettings({ ...settings, autoGenerateGameweeks: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-900 rounded-lg">
              <div>
                <label className="text-white font-medium">Auto-generate Rankings</label>
                <p className="text-sm text-gray-400">Automatically generate monthly/annual rankings</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.autoGenerateRankings}
                  onChange={(e) => setSettings({ ...settings, autoGenerateRankings: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Auto-generate Weeks Count</label>
              <input
                type="number"
                min="1"
                max="52"
                value={settings.autoGenerateWeeksCount}
                onChange={(e) => setSettings({ ...settings, autoGenerateWeeksCount: parseInt(e.target.value) || 4 })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>
        </div>
      </div>

      {hasChanges && (
        <div className="mt-6 p-4 bg-yellow-900/20 border border-yellow-700 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-yellow-400 mr-2" />
            <p className="text-yellow-300">You have unsaved changes. Don't forget to save!</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;
